import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "./Reveal";
import { MapPin, Phone, Clock, Instagram, Music2, MessageCircle } from "lucide-react";

const FALLBACK = {
  address: "12 rue des Fleurs, 75004 Paris",
  phone: "01 84 80 20 20",
  instagram: "@beautybyarmina",
  instagram_url: "https://instagram.com/beautybyarmina",
  tiktok: "@beautybyarmina",
  tiktok_url: "https://tiktok.com/@beautybyarmina",
  whatsapp_url: "https://wa.me/33601020304",
  hours: "Mardi – Samedi · 9h00 – 19h00",
};

export default function Contact() {
  const [salon, setSalon] = useState(FALLBACK);

  useEffect(() => {
    api.get("/salon").then((res) => setSalon(res.data)).catch(() => {});
  }, []);

  const go = (e) => {
    e.preventDefault();
    document.querySelector("#reservation")?.scrollIntoView({ behavior: "smooth" });
  };

  const items = [
    { icon: MapPin, label: "Adresse", value: salon.address, testid: "contact-address" },
    { icon: Phone, label: "Téléphone", value: salon.phone, href: `tel:${salon.phone.replace(/\s/g, "")}`, testid: "contact-phone" },
    { icon: Clock, label: "Horaires", value: salon.hours, testid: "contact-hours" },
    { icon: Instagram, label: "Instagram", value: salon.instagram, href: salon.instagram_url, testid: "contact-instagram" },
    { icon: Music2, label: "TikTok", value: salon.tiktok || "@beautybyarmina", href: salon.tiktok_url, testid: "contact-tiktok" },
    { icon: MessageCircle, label: "WhatsApp", value: "Écrivez-nous", href: salon.whatsapp_url, testid: "contact-whatsapp" },
  ];

  return (
    <section id="contact" className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-40" data-testid="contact-section">
      <Reveal>
        <Overline className="mb-6">Contact</Overline>
        <h2 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--brand-dark)]">
          L'institut vous attend, <span className="italic font-light text-[var(--brand-accent)]">au cœur de Paris.</span>
        </h2>
      </Reveal>

      <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const inner = (
            <>
              <item.icon size={22} strokeWidth={1.5} className="text-[var(--brand-accent)]" />
              <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[var(--brand-muted)]">{item.label}</p>
              <p className="mt-3 text-lg font-light text-[var(--brand-dark)]">{item.value}</p>
            </>
          );
          return (
            <Reveal key={item.label} delay={i * 0.08}>
              {item.href ? (
                <a href={item.href} target={item.href.startsWith("tel") ? undefined : "_blank"} rel="noopener noreferrer"
                   className="group flex h-full flex-col border border-black/5 bg-[var(--brand-section)] p-10 transition-colors duration-500 hover:bg-[var(--brand-border)]"
                   data-testid={item.testid}>
                  {inner}
                </a>
              ) : (
                <div className="flex h-full flex-col border border-black/5 bg-[var(--brand-section)] p-10" data-testid={item.testid}>
                  {inner}
                </div>
              )}
            </Reveal>
          );
        })}
      </div>

      <Reveal className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="overflow-hidden border border-black/5 lg:col-span-2" data-testid="contact-map">
          <iframe title="Localisation de l'institut Beauty by Armina"
                  src="https://www.google.com/maps?q=12%20rue%20des%20Fleurs%2075004%20Paris&output=embed"
                  className="h-80 w-full lg:h-full" loading="lazy" />
        </div>
        <div className="flex flex-col justify-between bg-[var(--brand-dark)] p-10 text-[var(--brand-bg)]">
          <div>
            <p className="font-display text-3xl leading-snug">Prête pour votre <span className="italic font-light text-[var(--brand-accent)]">parenthèse beauté ?</span></p>
            <p className="mt-4 text-sm font-light leading-relaxed text-[var(--brand-bg)]/65">
              Choisissez votre prestation et votre professionnelle en quelques clics. Confirmation immédiate par email.
            </p>
          </div>
          <a href="#reservation" onClick={go}
             className="mt-10 rounded-full bg-[var(--brand-bg)] px-8 py-4 text-center text-xs uppercase tracking-[0.25em] text-[var(--brand-dark)] transition-transform duration-300 hover:scale-[0.97]"
             data-testid="contact-booking-btn">
            Prendre rendez-vous
          </a>
        </div>
      </Reveal>
    </section>
  );
}
